import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NovaAvatarImg from '../components/NovaAvatarImg';
import { btnPrimary, btnGhost, tabNavContainer, tabNavItem, linkInline } from '../designSystem';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";
const FONT_SERIF = "'Playfair Display', Georgia, serif";

const R = 14;
const R_LG = 20;

const DEMO_PROFILE = {
  name: 'Jamie Mitchell',
  handle: 'jamie_mitchell',
  profilePhoto: '/images/chapters/fertility.png',
  coverPhoto: '/images/chapters/fertility.png',
  matchPct: 94,
  bio: 'IVF mama hopeful. Cycle 2, stim day 8. Ohio Fertility Network. Low AMH. Antagonist protocol. Grateful for this community.',
  location: 'Columbus, OH',
  clinic: 'Ohio Fertility Network',
  joined: 'Jan 2025',
  chapter: 'Fertility',
  diagnosis: 'Low AMH',
  protocol: 'Antagonist',
  tags: ['Same stim day as you', 'Same clinic as you'],
  stats: { posts: 24, hearts: 156, friends: 47, cycles: 2, embryos: 0 },
  stage: 'Stim day 8',
  sharedDetails: 'low AMH, antagonist protocol, Ohio Fertility Network',
  sharedExperience: 'a failed first retrieval',
  friends: [
    { name: 'Jasmine M.', sub: 'Cycle 2 · stim day 8', online: true, bg: '#FBEAF0', color: '#993556' },
    { name: 'Ava K.', sub: 'Transfer prep', online: true, bg: '#E6F1FB', color: '#185FA5' },
    { name: 'Noelle R.', sub: 'Stims day 8 · Cleveland', online: false, bg: '#FBEAF0', color: '#993556' },
    { name: 'Emma T.', sub: 'IVF cycle 2', online: true, bg: '#EEEDFE', color: '#534AB7' },
    { name: 'Sofia L.', sub: 'Postpartum', online: false, bg: '#E1F5EE', color: '#0F6E56' },
  ],
  friendsCount: 47,
  journeyStats: {
    ivfCycles: 2,
    transfers: 1,
    pregnancies: 1,
    eggsRetrieved: 12,
    embryosBanked: 0,
    losses: 1,
    liveBirths: 0,
    tryingSince: '2023',
    diagnosis: 'Low AMH',
    protocol: 'Antagonist',
  },
  cycleTimeline: [
    { name: 'Cycle 1', sub: 'Retrieval · 8 eggs', date: 'Nov 2024', status: 'loss' },
    { name: 'Cycle 2', sub: 'Stims · Day 8', date: 'Mar 2025', status: 'active' },
  ],
  quote: { text: 'Hope is the thing with feathers that perches in the soul.', author: '— Emily Dickinson' },
  posts: [
    { name: 'Jamie', badge: 'Stim day 8', date: '2 days ago', text: 'Day 8 scan tomorrow. Nervous but ready. Anyone else at Ohio Fertility doing scans this week?', hearts: 12, sameBoat: 3, liked: false },
    { name: 'Jamie', badge: null, date: '1 week ago', text: 'First retrieval done. 8 eggs. Waiting on fertilization report. Fingers crossed.', hearts: 28, sameBoat: 5, liked: true },
  ],
  saved: [
    { title: 'Understanding your follicle count', source: 'RESOLVE', readTime: '4 min', category: 'labs', emoji: '🔬' },
    { title: 'Gonal-F vs Follistim: what to ask', source: 'Nova', readTime: '3 min', category: 'meds', emoji: '💊' },
    { title: 'Coping with retrieval anxiety', source: 'Nova', readTime: '5 min', category: 'mental', emoji: '🧘' },
  ],
  groups: [
    { name: 'IVF Warriors — Cycle 1', members: '3,240', live: 12 },
    { name: 'Low AMH Warriors', members: '2,100', live: 8 },
  ],
  badges: [
    { label: '2 cycles', bg: 'var(--pink-l)', color: 'var(--pink-dk)' },
    { label: '0 embryos', bg: 'var(--sage-l)', color: 'var(--sage-d)' },
    { label: 'Low AMH', bg: 'var(--purple-l)', color: 'var(--purple-d)' },
    { label: '24 posts', bg: '#FEF0EB', color: '#C94E22' },
    { label: '47 connections', bg: 'var(--sage-l)', color: 'var(--sage-d)' },
  ],
};

function initials(str) {
  const s = String(str || '').trim();
  if (!s) return '?';
  return s.split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
}

const tabNavItemProfile = (active) => ({
  ...tabNavItem(active),
  fontWeight: 600,
  color: active ? 'var(--pink-dk)' : 'var(--muted)',
  borderBottom: `2px solid ${active ? 'var(--pink)' : 'transparent'}`,
});

export default function ProfileDemo({ navigate }) {
  const { username } = useParams();
  const [activeTab, setActiveTab] = useState('posts');
  const profileNameMap = {
    jasmine: { name: 'Jasmine M.', handle: 'jasmine_m', stage: 'Stim day 8' },
    maya: { name: 'Maya', handle: 'maya', stage: 'Cycle 2' },
    rina: { name: 'Rina', handle: 'rina', stage: 'Trigger shot night' },
    priya: { name: 'Priya', handle: 'priya', stage: 'Post-retrieval' },
    ava: { name: 'Ava K.', handle: 'ava_k', stage: 'Transfer prep' },
    noelle: { name: 'Noelle R.', handle: 'noelle_r', stage: 'Stims day 8' },
    emma: { name: 'Emma T.', handle: 'emma_t', stage: 'IVF cycle 2' },
    sofia: { name: 'Sofia L.', handle: 'sofia_l', stage: 'Postpartum' },
  };
  const slug = String(username || '').toLowerCase();
  const profile = {
    ...DEMO_PROFILE,
    ...(profileNameMap[slug] || {}),
  };

  const sl = { fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' };
  const card = { background: 'var(--white)', border: '1px solid var(--border)', borderRadius: R_LG, overflow: 'hidden' };
  return (
    <div style={{ fontFamily: FONT_BODY, padding: '0 0 40px', width: '100%', maxWidth: 980, margin: '0 auto' }}>
      {/* ← Back to community */}
      <div style={{ marginBottom: 16 }}>
        <span
          onClick={() => navigate('/demo/fertility/community')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/demo/fertility/community')}
          style={{ fontSize: 12, fontWeight: 600, color: 'var(--pink)', cursor: 'pointer' }}
        >
          ← Back to community
        </span>
      </div>

      {/* Section 1 — Profile header card */}
      <div style={{ ...card, marginBottom: 16 }}>
        {/* Cover */}
        <div
          style={{
            height: 170,
            background: 'var(--sand-2)',
            position: 'relative',
          }}
        >
          {profile.coverPhoto ? (
            <img
              src={profile.coverPhoto}
              alt=""
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(244,192,209,0.4) 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(255,252,249,0.15) 0%, rgba(255,252,249,0.55) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56, opacity: 0.12 }}>🌸</div>
          <div style={{ position: 'absolute', left: 20, bottom: -48, width: 104, height: 104, borderRadius: '50%', border: '4px solid var(--white)', background: 'var(--pink-m)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {profile.profilePhoto ? (
              <img src={profile.profilePhoto} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--pink-dk)' }}>{initials(profile.name)}</span>
            )}
          </div>
        </div>
        {/* Avatar + actions */}
        <div style={{ padding: '60px 20px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => navigate('/signup')} style={btnPrimary}>Connect</button>
              <button type="button" onClick={() => navigate('/signup')} style={btnGhost}>Message</button>
              <button type="button" onClick={() => navigate('/signup')} style={btnGhost}>Follow</button>
            </div>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{profile.name}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
            @{profile.handle} · Fertility chapter · <span style={{ color: 'var(--pink)', fontWeight: 700 }}>{profile.matchPct}% match</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, maxWidth: 580, marginBottom: 8 }}>
            &ldquo;{profile.bio}&rdquo;
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
            📍 {profile.location} · 🏥 {profile.clinic} · 📅 Joined {profile.joined}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 0 }}>
            {[profile.chapter, profile.diagnosis, profile.protocol, ...profile.tags].map((t, i) => (
              <span
                key={i}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: ['var(--pink-l)', 'var(--purple-l)', 'var(--purple-l)', 'var(--sage-l)', 'var(--sage-l)'][i % 5],
                  color: ['var(--pink-dk)', 'var(--purple-d)', 'var(--purple-d)', 'var(--sage-d)', 'var(--sage-d)'][i % 5],
                  border: `1px solid ${['var(--pink-m)', 'var(--purple-m)', 'var(--purple-m)', 'var(--sage-m)', 'var(--sage-m)'][i % 5]}`,
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
        {/* Stats bar */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
          {[
            { label: 'IVF cycles', value: profile.journeyStats.ivfCycles, color: 'var(--pink)' },
            { label: 'Transfers', value: profile.journeyStats.transfers, color: 'var(--text)' },
            { label: 'Pregnancies', value: profile.journeyStats.pregnancies, color: 'var(--text)' },
            { label: 'Live births', value: profile.journeyStats.liveBirths, color: 'var(--sage-d)' },
            { label: 'Miscarriages', value: profile.journeyStats.losses, color: 'var(--pink-dk)' },
          ].map((s, i) => (
            <div key={s.label} style={{ padding: '12px 8px', borderRight: i < 4 ? '1px solid var(--sand-2)' : 'none' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>{s.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2 — Nova match banner */}
      <div
        style={{
          background: 'var(--purple-l)',
          border: '1px solid var(--purple-m)',
          borderRadius: R,
          padding: '13px 16px',
          marginBottom: 16,
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
        }}
      >
        <NovaAvatarImg size={26} alt="" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--purple-d)', lineHeight: 1.6, marginBottom: 10 }}>
            Nova matched you at {profile.matchPct}%. {profile.name.split(' ')[0]} is {profile.stage.toLowerCase()} — same as you. {profile.sharedDetails}. She also went through {profile.sharedExperience}.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {['What do we have in common?', `What did she share about ${profile.stage.toLowerCase()}?`, 'Ask Nova about this clinic'].map((prompt, i) => (
              <button
                key={i}
                type="button"
                onClick={() => navigate('/nova')}
                style={{
                  background: 'var(--white)',
                  border: '1px solid var(--purple-m)',
                  color: 'var(--purple-d)',
                  borderRadius: 999,
                  padding: '6px 12px',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Section 3 — Friends row */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={sl}>Friends · {profile.friendsCount}</div>
          <span onClick={() => navigate('/signup')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/signup')} style={{ ...linkInline, fontSize: 12 }}>See all →</span>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {profile.friends.map((f, i) => (
            <div
              key={i}
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--white)',
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '6px 12px 6px 6px',
                position: 'relative',
              }}
            >
              <div style={{ position: 'relative' }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
                  {initials(f.name)}
                </div>
                {f.online && <div style={{ position: 'absolute', bottom: 0, right: 0, width: 7, height: 7, borderRadius: '50%', background: '#22c55e', border: '1.5px solid white' }} />}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{f.name}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)' }}>{f.sub}</div>
              </div>
            </div>
          ))}
          <div
            onClick={() => navigate('/signup')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/signup')}
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', padding: '6px 12px', border: '1px dashed var(--border)', borderRadius: 999, cursor: 'pointer', fontSize: 11, color: 'var(--muted)' }}
          >
            +{profile.friendsCount - profile.friends.length} more →
          </div>
        </div>
      </div>

      {/* Section 4 — Tab bar + panels */}
      <div style={card}>
        <div style={{ ...tabNavContainer, overflowX: 'auto' }}>
          {[
            { key: 'posts', label: 'Posts' },
            { key: 'journey', label: 'Journey' },
            { key: 'photos', label: 'Photos' },
            { key: 'saved', label: 'Saved' },
            { key: 'about', label: 'About' },
          ].map((t) => (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} style={tabNavItemProfile(activeTab === t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 20 }}>
          {/* Posts tab */}
          {activeTab === 'posts' && (
            <>
              <div style={{ background: 'var(--pink-l)', border: '1px solid var(--pink-m)', borderRadius: R, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pink-dk)', lineHeight: 1.4 }}>
                  &ldquo;{profile.quote.text}&rdquo;
                </div>
                <div style={{ fontSize: 11, color: 'var(--pink-d)', marginTop: 2 }}>{profile.quote.author}</div>
              </div>
              {profile.posts.map((p, i) => (
                <div key={i} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, marginBottom: 10, padding: '16px 20px', display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#FBEAF0', color: '#993556', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0 }}>
                    {initials(profile.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#d4537e' }}>{profile.name}</span>
                      {p.badge && (
                        <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid rgba(29,158,117,0.2)' }}>
                          {p.badge}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'rgba(60,30,45,0.38)', marginLeft: 'auto' }}>{p.date}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.5 }}>{p.text}</div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                      <span>❤️ {p.hearts}</span>
                      <span>🤝 same boat {p.sameBoat}</span>
                      <span>💬 Reply</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Journey tab */}
          {activeTab === 'journey' && (
            <>
              <div style={{ ...sl, marginBottom: 10 }}>Journey at a glance</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'IVF cycles', value: profile.journeyStats.ivfCycles, sub: '', color: 'var(--pink)' },
                  { label: 'Eggs retrieved', value: profile.journeyStats.eggsRetrieved, sub: '', color: 'var(--text)' },
                  { label: 'Embryos banked', value: profile.journeyStats.embryosBanked, sub: '', color: 'var(--sage-d)' },
                  { label: 'Losses', value: profile.journeyStats.losses, sub: '', color: 'var(--pink)' },
                  { label: 'Live births', value: profile.journeyStats.liveBirths, sub: '', color: 'var(--sage-d)' },
                  { label: 'Trying since', value: profile.journeyStats.tryingSince, sub: '', color: 'var(--purple-d)' },
                  { label: 'Diagnosis', value: profile.journeyStats.diagnosis, sub: '', color: 'var(--text)' },
                  { label: 'Protocol', value: profile.journeyStats.protocol, sub: '', color: 'var(--text)' },
                ].map((s, i) => (
                  <div key={s.label} style={{ background: 'var(--sand)', borderRadius: 9, padding: '9px 11px' }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
                    {s.sub && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{s.sub}</div>}
                  </div>
                ))}
              </div>
              <div style={{ ...sl, marginBottom: 10 }}>Cycle timeline</div>
              <div style={{ position: 'relative', paddingLeft: 24 }}>
                {profile.cycleTimeline.map((c, i) => (
                  <div key={i} style={{ position: 'relative', paddingBottom: i < profile.cycleTimeline.length - 1 ? 16 : 0 }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: -24,
                        top: 4,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: c.status === 'active' ? 'var(--pink)' : c.status === 'loss' ? 'var(--pink-l)' : 'var(--sage-l)',
                        border: c.status === 'active' ? 'none' : '1px solid var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        color: c.status === 'active' ? 'white' : c.status === 'loss' ? 'var(--pink-dk)' : 'var(--sage-d)',
                      }}
                    >
                      {c.status === 'completed' ? '✓' : c.status === 'active' ? '→' : ''}
                    </div>
                    {i < profile.cycleTimeline.length - 1 && <div style={{ position: 'absolute', left: -13, top: 28, bottom: 0, width: 2, background: 'var(--sand-2)' }} />}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.sub}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{c.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Photos tab */}
          {activeTab === 'photos' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {['#f5e8e0', '#e8f0f5', '#edf5e8', '#f5ecf0', '#f0eef5', '#f5f0e8', '#e8f5f0', '#f5e8f0'].map((bg, i) => (
                  <div key={i} style={{ aspectRatio: 1, borderRadius: 9, background: bg }} />
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 10 }}>8 photos · tap to view</div>
            </>
          )}

          {/* Saved tab */}
          {activeTab === 'saved' && (
            <>
              <div style={sl}>Saved articles</div>
              {profile.saved.map((a, i) => (
                <div
                  key={i}
                  onClick={() => navigate('/resources')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/resources')}
                  style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: i < profile.saved.length - 1 ? '1px solid var(--sand-2)' : 'none', cursor: 'pointer' }}
                >
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--sand-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{a.emoji}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        marginRight: 6,
                        background: { labs: 'var(--purple-l)', meds: '#FEF0EB', mental: '#E1F5EE', finances: 'var(--pink-l)' }[a.category] || 'var(--sand-2)',
                        color: { labs: 'var(--purple-d)', meds: '#C94E22', mental: 'var(--sage-d)', finances: 'var(--pink-dk)' }[a.category] || 'var(--muted)',
                      }}
                    >
                      {a.category}
                    </span>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>{a.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{a.source} · {a.readTime}</div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* About tab */}
          {activeTab === 'about' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: 'var(--sand)', borderRadius: R, padding: 16 }}>
                <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 16 }}>{profile.bio}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { key: 'Location', val: profile.location },
                    { key: 'Chapter', val: profile.chapter },
                    { key: 'Clinic', val: profile.clinic },
                    { key: 'Open to', val: 'DMs, same-stage connections' },
                  ].map((r) => (
                    <div key={r.key} style={{ display: 'flex', gap: 8 }}>
                      <span style={{ width: 80, fontSize: 12, color: 'var(--muted)' }}>{r.key}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{r.val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: R, padding: 16 }}>
                  <div style={{ ...sl, marginBottom: 10 }}>Groups</div>
                  {profile.groups.map((g, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < profile.groups.length - 1 ? '1px solid var(--sand-2)' : 'none' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--pink-l)', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{g.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{g.members} members · <span style={{ color: 'var(--sage-d)' }}>{g.live} in live room</span></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: R, padding: 16 }}>
                  <div style={{ ...sl, marginBottom: 10 }}>Milestone badges</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.badges.map((b, i) => (
                      <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: b.bg, color: b.color }}>{b.label}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
